// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: several named pads inside the one saved document. The server keeps a single
// markdown string per user; pads are sections of it split by marker lines:
//
//   <!-- pad: Today -->
//   ...markdown...
//   <!-- pad: Ideas -->
//   ...markdown...
//
// A document without markers is one pad called "Notes", and stays marker-free until a
// second pad is added, so nobody sees markers they never asked for.
//
// A pad with a whiteboard carries the board's id in its marker, so renaming the pad keeps
// the board: `<!-- pad: Kiosk redesign | board:k3x9q2m7 -->`. Such a pad always has a
// marker, even when it is the only one.

export const DEFAULT_PAD = 'Notes';

/**
 * A pad's name as shown. The first pad is stored as "Notes" but shown as "My notes", so it
 * cannot be mistaken for the Notes side of the Notes | Board switch.
 */
export function padLabel(name: string): string {
	return name === DEFAULT_PAD ? 'My notes' : name;
}
export const MAX_PADS = 12;
export const MAX_PAD_NAME = 32;

const MARKER = /^<!--\s*pad:\s*(.+?)\s*-->\s*$/;
const BOARD_SUFFIX = /\s*\|\s*board:([a-z0-9]{6,32})\s*$/;

export interface Pad {
	name: string;
	content: string;
	/** Id of the pad's whiteboard, once it has one. */
	board?: string;
}

/** Splits a marker's text into the pad name and its board id. */
function readMarker(raw: string): {name: string; board?: string} {
	const match = BOARD_SUFFIX.exec(raw);
	if (!match) return {name: raw};
	return {name: raw.slice(0, match.index).trim() || DEFAULT_PAD, board: match[1]};
}

function writeMarker(pad: Pad): string {
	return `<!-- pad: ${pad.name}${pad.board ? ` | board:${pad.board}` : ''} -->`;
}

/** A fresh board id: lowercase letters and digits, as BOARD_ID_PATTERN expects. */
export function newBoardId(): string {
	const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
	const bytes = new Uint8Array(10);
	crypto.getRandomValues(bytes);
	return Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join('');
}

export function parsePads(document: string): Array<Pad> {
	const lines = document.split('\n');
	const pads: Array<Pad> = [];
	let current: {name: string; board?: string; lines: Array<string>} | null = null;
	let preamble: Array<string> = [];
	const close = (pad: {name: string; board?: string; lines: Array<string>}) =>
		pads.push({name: pad.name, content: pad.lines.join('\n'), ...(pad.board ? {board: pad.board} : {})});
	for (const line of lines) {
		const match = MARKER.exec(line);
		if (match) {
			if (current) close(current);
			current = {...readMarker(match[1]), lines: []};
			continue;
		}
		if (current) current.lines.push(line);
		else preamble.push(line);
	}
	if (current) close(current);
	const stray = preamble.join('\n');
	if (pads.length === 0) return [{name: DEFAULT_PAD, content: stray}];
	// Text above the first marker belongs to the first pad rather than vanishing.
	if (stray.trim().length > 0) {
		const first = pads[0];
		pads[0] = {...first, content: first.content ? `${stray}\n${first.content}` : stray};
	}
	preamble = [];
	return pads;
}

export function serialisePads(pads: ReadonlyArray<Pad>): string {
	if (pads.length === 1 && pads[0].name === DEFAULT_PAD && !pads[0].board) return pads[0].content;
	return pads.map((pad) => `${writeMarker(pad)}\n${pad.content.replace(/\n+$/, '')}`).join('\n');
}

/** A name that is safe to put in a marker and unique among `pads`. */
export function cleanPadName(raw: string, pads: ReadonlyArray<Pad>, current?: string): string | null {
	const name = raw
		.replace(/[<>|\n]/g, '')
		.trim()
		.slice(0, MAX_PAD_NAME);
	if (!name) return null;
	// Compared with how pads are shown too, so no second pad can look like "My notes".
	const taken = (pad: Pad) =>
		[pad.name, padLabel(pad.name)].some((shown) => shown.toLowerCase() === name.toLowerCase());
	if (pads.some((pad) => pad.name !== current && taken(pad))) return null;
	return name;
}
